import React from 'react';
import { useMediaQuery } from 'react-responsive';

/**
 * Tells whether a table is too wide for the current screen and should be shown as cards instead.
 * The wider the screen, the more columns a table may carry before it stops being readable.
 */
export const showCard = (
  columnCount: number,
  isMobile: boolean,
  isTablet: boolean,
  isDesktop: boolean,
  isLargeDesktop: boolean,
) => {
  return (
    isMobile || (isTablet && columnCount > 4) || (isDesktop && columnCount > 10) || (isLargeDesktop && columnCount > 20)
  );
};

/**
 * Counts the columns of a rendered markdown table (the largest number of cells any of its rows holds).
 */
export const getColumnCount = (children: React.ReactNode): number => {
  return React.Children.toArray(children).reduce((count, child) => {
    if (React.isValidElement(child) && child.props.children) {
      return Math.max(count as number, React.Children.count(child.props.children));
    }
    return count;
  }, 0) as number;
};

/**
 * Whether the given table should be rendered as cards at the current viewport width.
 *
 * It reads the viewport through `useMediaQuery`, so it follows the rules of hooks.
 */
export const shouldShowCard = (children: React.ReactNode) => {
  const columnCount = getColumnCount(children);

  const isMobile = useMediaQuery({ maxWidth: '53.125rem' });
  const isTablet = useMediaQuery({ minWidth: '53.125rem', maxWidth: '64rem' });
  const isDesktop = useMediaQuery({ minWidth: '64rem', maxWidth: '150rem' });
  const isLargeDesktop = useMediaQuery({ minWidth: '150rem' });

  return showCard(columnCount, isMobile, isTablet, isDesktop, isLargeDesktop);
};

/**
 Tries to turn a link's label into a plain string.
 */
export const toLinkText = (children: React.ReactNode): string =>
  React.Children.toArray(children)
    .map((child) => (typeof child === 'string' || typeof child === 'number' ? String(child) : ''))
    .join('');
